import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import { Spinner, Modal, Button, Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import { toast } from "react-toastify";
import { ADMIN_TUTOR_API } from "../../../api/api";

import Announcementsimg from "../../../assets/announcements-empty.png";
import Rejected from "../../../assets/admin-tutor-rejected-icon.svg";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import { IoArrowBack } from "react-icons/io5";

import "./AdminRejectedTutorManagement.css";

const AdminRejectedTutorManagement = () => {
  const { tutorId } = useParams();
  const navigate = useNavigate();

  const [rejectedTutors, setRejectedTutors] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  const [showModal, setShowModal] = useState(false);

  //loading
  const [isLoadingTutors, setIsLoadingTutors] = useState(true);

  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchRejectedTutors = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setIsLoadingTutors(false);
      navigate("/admin-signin");
      return;
    }

    try {
      let response;
      if (tutorId) {
        // Fetch single tutor
        response = await axios.post(
          ADMIN_TUTOR_API.GET_TUTOR_DETAILS_BY_STATUS_ID,
          { status: "Rejected", tutor_id: tutorId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        // Fetch all rejected tutors
        response = await axios.post(
          ADMIN_TUTOR_API.GET_TUTOR_DETAILS_BY_STATUS,
          { status: "Rejected" },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      const data = response.data;

      if (data.statusCode === 200) {
        setRejectedTutors(data.data || []);
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    } finally {
      setIsLoadingTutors(false);
    }
  }, [tutorId, navigate]);

  useEffect(() => {
    fetchRejectedTutors();
  }, [fetchRejectedTutors]);

  const handleViewDetailsModal = (data) => {
    setSelectedData(data);
    setShowModal(true);
  };

  const handleReconsiderTutorSubmit = async () => {
    if (!selectedData?.tutor_id) return; // safety check

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-signin");
      return;
    }

    try {
      const response = await axios.post(
        ADMIN_TUTOR_API.UPDATE_TUTOR_STATUS,
        {
          tutor_id: selectedData.tutor_id,
          status: "Pending",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;
      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        navigate("/header/admin/tutor-pending");
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error(err);

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/admin-signin");
    }
  };

  return (
    <Container className="admin-tutor-reject-wrapper">
      <div className="admin-pending-backmoving-button-wrapper">
        <button
          className="admin-pending-backmoving-button-main"
          onClick={() => navigate("/header/admin/all-tutor")}
        >
          <IoArrowBack className="admin-pending-backmoving-button-icon" />
          <span className="admin-pending-backmoving-button-text">Back</span>
        </button>
      </div>
      <h4 className="admin-dashboard-title">Rejected Tutor Applications</h4>
      <p className="admin-dashboard-subtitle">
        Review rejected tutor applications, review feedback, and reconsider them
        when needed
      </p>

      {/* ALERT */}
      {!isLoadingTutors && rejectedTutors.length > 0 && (
        <div className="admin-tutor-reject-alert">
          <img src={Rejected} alt="Rejected" />
          <div>
            <h6>Rejection History</h6>
            <p>
              These applications were previously rejected with feedback. You can
              reconsider any application to move it back to pending review.
            </p>
          </div>
        </div>
      )}

      <div className="admin-tutor-reject-grid">
        {isLoadingTutors ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : rejectedTutors.length > 0 ? (
          rejectedTutors.map((tutor) => (
            <div className="admin-tutor-reject-card" key={tutor.tutor_id}>
              <div className="admin-tutor-reject-header mb-3">
                <div>
                  <h6>{tutor.full_name || "N/A"}</h6>
                  <span className="admin-tutor-reject-role">
                    {tutor.subject_to_teach || "N/A"}
                  </span>
                </div>
                <span className="admin-tutor-reject-status">
                  {tutor.status || "N/A"}
                </span>
              </div>

              <div className="admin-tutor-reject-meta mb-3">
                <div className="admin-tutor-reject-meta-box">
                  <span className="admin-tutor-reject-meta-label">Email</span>
                  <span className="admin-tutor-reject-meta-value">
                    {tutor.email || "N/A"}
                  </span>
                </div>
                <div className="admin-tutor-reject-meta-box">
                  <span className="admin-tutor-reject-meta-label">
                    Rejected Date
                  </span>
                  <span className="admin-tutor-reject-meta-value">
                    {tutor.rejected_date || "N/A"}
                  </span>
                </div>
              </div>

              <div className="admin-tutor-reject-meta mb-3">
                <div className="admin-tutor-reject-meta-box">
                  <span className="admin-tutor-reject-meta-label">
                    Rejected By
                  </span>
                  <span className="admin-tutor-reject-meta-value">Admin</span>
                </div>
                <div className="admin-tutor-reject-meta-box-1">
                  <span className="admin-tutor-reject-meta-label">
                    Reason for Rejection
                  </span>
                  <span className="admin-tutor-reject-meta-value">
                    {tutor.reject_reason || "No reject reason provided"}
                  </span>
                </div>
              </div>

              <div className="admin-tutor-reject-meta-1 mb-3">
                <div className="admin-tutor-reject-meta-box">
                  <span className="admin-tutor-reject-meta-label">
                    Description
                  </span>
                  <span className="admin-tutor-reject-meta-value">
                    {tutor.short_bio || "N/A"}
                  </span>
                </div>
              </div>

              <Button
                className="admin-tutor-reject-view-btn"
                onClick={() => handleViewDetailsModal(tutor)}
              >
                View Details
              </Button>
            </div>
          ))
        ) : (
          <div className="tutor-assignment-empty-box">
            <div className="tutor-assignment-empty-content">
              <img
                src={Announcementsimg}
                alt="empty"
                className="tutor-announcements-section-empty-img mb-3"
              />
              <h5>No rejected tutors found.</h5>
            </div>
          </div>
        )}
      </div>

      {selectedData && (
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          size="md"
          className="admin-reject-modal"
        >
          <Modal.Header closeButton className="admin-reject-modal-header">
            <div className="admin-reject-modal-header-content">
              <Modal.Title className="admin-review-modal-header-title">
                Rejection Tutor Details
              </Modal.Title>
              <p className="admin-review-modal-header-paragraph">
                Review the rejected tutor application, verify the provided
                information, and reconsider it for approval.
              </p>
            </div>
          </Modal.Header>

          <Modal.Body className="admin-reject-modal-header-body">
            {/* USER INFO CARD */}
            <div className="admin-reject-modal-header-user-card">
              <div className="admin-reject-modal-header-user-avatar">
                {" "}
                {initialsFromName(selectedData.full_name)}
              </div>
              <div>
                <h6>{selectedData.full_name || "N/A"}</h6>
                <p>{selectedData.email || "N/A"}</p>
              </div>
            </div>

            {/* GRID INFO */}
            <div className="admin-reject-modal-info-grid">
              <div className="admin-reject-modal-info-box">
                <span className="admin-reject-modal-info-label">
                  Rejected By
                </span>
                <span className="admin-reject-modal-info-value">Admin</span>
              </div>

              <div className="admin-reject-modal-info-box">
                <span className="admin-reject-modal-info-label">
                  Rejected Date
                </span>
                <span className="admin-reject-modal-info-value">
                  {selectedData.rejected_date || "N/A"}
                </span>
              </div>
            </div>

            <div className="admin-reject-modal-info-box mb-3">
              <span className="admin-reject-modal-info-label">Course</span>
              <span className="admin-reject-modal-info-value">
                {selectedData.subject_to_teach || "N/A"}
              </span>
            </div>

            {/* REJECTION REASON */}
            <div className="admin-reject-modal-reason-box">
              <span className="admin-reject-modal-reason-title">
                Reason for Rejection
              </span>
              <p>{selectedData.reject_reason || "N/A"}</p>
            </div>
          </Modal.Body>

          <Modal.Footer className="admin-reject-modal-footer">
            <Button
              className="admin-reject-modal-btn-reconsider"
              onClick={handleReconsiderTutorSubmit}
            >
              Reconsider Application
            </Button>
            <Button
              className="admin-reject-modal-btn-close-modal"
              onClick={() => setShowModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default AdminRejectedTutorManagement;
